document.querySelectorAll('[data-vc-about-slider-track]').forEach((track) => {
  const inner = track.querySelector('[data-vc-about-slider-inner]');
  const realSlides = Array.from(inner.querySelectorAll('[data-vc-about-slider-slide]'));
  const dotsContainer = track.closest('.vc-about-slider__wrapper')?.querySelector('[data-vc-about-slider-dots]');
  const realCount = realSlides.length;
  if (!realCount) return;

  // Clone both ends to reproduce Slick's infinite track at every breakpoint.
  const cloneSet = () => realSlides.map((slide) => {
    const clone = slide.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-vc-about-slider-clone', '');
    clone.querySelectorAll('[data-shopify-editor-block]').forEach((el) => el.removeAttribute('data-shopify-editor-block'));
    return clone;
  });

  const leadingClones = cloneSet();
  const trailingClones = cloneSet();
  // Prepend the leading set in one call so clone order is preserved.
  inner.prepend(...leadingClones);
  trailingClones.forEach((clone) => inner.appendChild(clone));

  const allSlides = Array.from(inner.children);
  const clonesCount = realCount;

  const dots = realSlides.map((_, index) => {
    const dot = document.createElement('button');
    dot.type = 'button';
    dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
    dot.addEventListener('click', () => goTo(clonesCount + index, true));
    dotsContainer?.appendChild(dot);
    return dot;
  });

  // Slide widths are pure arithmetic (read from the CSS custom properties in
  // vc-about-slider.liquid, which must stay in sync with the actual `width` rules per
  // breakpoint) rather than measured via getBoundingClientRect -- immediately after changing
  // a slide's active/adjacent state, a synchronous layout read reflects the OLD (pre-transition)
  // box, not the target one, so measuring would require racy transition-disabling tricks. Pure
  // math sidesteps that entirely and also lets drag reactively preview resizing without any
  // DOM measurement per frame.
  let restW = 0;
  let adjW = 0;
  let centerW = 0;

  const readWidths = () => {
    const cs = getComputedStyle(track);
    restW = parseFloat(cs.getPropertyValue('--slide-rest-w')) || 192;
    adjW = parseFloat(cs.getPropertyValue('--slide-adjacent-w')) || 232;
    centerW = parseFloat(cs.getPropertyValue('--slide-center-w')) || 312;
  };

  const widthAt = (index, activeIndex) => {
    if (index === activeIndex) return centerW;
    if (Math.abs(index - activeIndex) === 1) return adjW;
    return restW;
  };

  // Center-point (in px from the start of .track-inner) of slide `index`, in a layout where
  // `activeIndex` is the one currently sized as centerW/adjW.
  const centerOf = (index, activeIndex) => {
    let x = 0;
    for (let k = 0; k < index; k++) x += widthAt(k, activeIndex);
    return x + widthAt(index, activeIndex) / 2;
  };

  const idealTranslateX = (activeIndex) => track.clientWidth / 2 - centerOf(activeIndex, activeIndex);

  const applyActiveClasses = (activeIndex) => {
    allSlides.forEach((slide, index) => {
      if (index === activeIndex) slide.setAttribute('data-vc-active', 'center');
      else if (Math.abs(index - activeIndex) === 1) slide.setAttribute('data-vc-active', 'adjacent');
      else slide.removeAttribute('data-vc-active');
    });
    const realIndex = ((activeIndex - clonesCount) % realCount + realCount) % realCount;
    dots.forEach((dot, index) => dot.setAttribute('aria-current', index === realIndex ? 'true' : 'false'));
  };

  let activeIndex = clonesCount; // real slide 0, matching live's own default (verified: Slick's
  // initial centered slide is index 0, not the middle -- confirmed via live's dot state
  // immediately on a fresh page load, before autoplay has had a chance to advance it).

  const setTransform = (x, animate) => {
    inner.style.transition = animate ? '' : 'none';
    inner.style.transform = `translateX(${x}px)`;
  };

  // After any settle, silently (no animation) remap a clone-zone activeIndex back onto the
  // equivalent real slide -- both render the identical image at the identical relative
  // position, so the TRANSFORM jump is visually imperceptible, the same illusion Slick's own
  // cloning produces. Real bug fixed: the slide/img elements have their OWN separate `width`
  // transition (0.6s, longer than inner's 0.5s transform transition), driven purely by the
  // `data-vc-active` attribute `applyActiveClasses` sets here. The newly-active real slide was
  // sitting at "rest" width (192px) right up until this exact instant, so flipping its attribute
  // let its width transition run normally -- meaning it visibly SHRINKS to rest width, then
  // regrows to center width over the next ~550ms, even though the transform already jumped it
  // straight to the (correct, on-screen) center slot. `.is-settling` forces every width
  // transition off for just this one swap (with a forced-reflow on each side, the standard FLIP
  // pattern, so the browser actually commits "no transition" before the values change and commits
  // the new values before transitions are re-enabled) so the width and position land together in
  // the same frame -- matching the comment's original intent, which the code never actually
  // delivered on.
  const settleIfInCloneZone = () => {
    if (activeIndex >= clonesCount && activeIndex < clonesCount + realCount) return;
    const realIndex = ((activeIndex - clonesCount) % realCount + realCount) % realCount;
    activeIndex = clonesCount + realIndex;
    inner.classList.add('is-settling');
    void inner.offsetWidth;
    applyActiveClasses(activeIndex);
    setTransform(idealTranslateX(activeIndex), false);
    void inner.offsetWidth;
    inner.classList.remove('is-settling');
  };

  // goTo can be re-entered before a previous animated call's transition finishes (e.g. autoplay's
  // timer elapsing while a dot-click's own transition is still running -- dots, unlike drag, don't
  // pause/reschedule autoplay around themselves). Changing `transform` mid-transition cancels the
  // old transition WITHOUT firing `transitionend` for it, so a plain `addEventListener(...,
  // {once:true})` on every call would leave that stale listener dangling until some later
  // transition happens to complete uninterrupted -- at which point every pile-up listener fires
  // together. `settleIfInCloneZone` currently tolerates that (it keys off the live `activeIndex`
  // closure var, not a per-call snapshot, so a repeat call is a harmless no-op), but that safety is
  // incidental. Explicitly clearing the previous pending listener here guarantees at most one
  // pending settle at a time, always tied to the latest call -- pure bookkeeping, no change to
  // transition duration/easing/final position or autoplay/drag timing.
  let pendingSettle = null;
  const goTo = (index, animate) => {
    activeIndex = index;
    applyActiveClasses(activeIndex);
    setTransform(idealTranslateX(activeIndex), animate);
    if (pendingSettle) {
      inner.removeEventListener('transitionend', pendingSettle);
      pendingSettle = null;
    }
    if (animate) {
      pendingSettle = () => {
        pendingSettle = null;
        settleIfInCloneZone();
      };
      inner.addEventListener('transitionend', pendingSettle, { once: true });
    } else {
      settleIfInCloneZone();
    }
  };

  // Autoplay -- live runs Slick with autoplay:true, autoplaySpeed:4000, pauseOnHover:true. Our
  // previous build had no autoplay at all.
  const AUTOPLAY_MS = 4000;
  let autoplayTimer = null;
  let autoplayEnabled = false;
  let isHovering = false;
  let isPointerDown = false;

  const stopAutoplay = () => {
    if (autoplayTimer) clearTimeout(autoplayTimer);
    autoplayTimer = null;
  };

  const scheduleAutoplay = () => {
    stopAutoplay();
    if (!autoplayEnabled || isHovering || isPointerDown) return;
    autoplayTimer = setTimeout(() => {
      goTo(activeIndex + 1, true);
      scheduleAutoplay();
    }, AUTOPLAY_MS);
  };

  const io = new IntersectionObserver((entries) => {
    autoplayEnabled = entries[0]?.isIntersecting ?? false;
    scheduleAutoplay();
  }, { threshold: 0.2 });
  io.observe(track);

  track.addEventListener('mouseenter', () => {
    isHovering = true;
    stopAutoplay();
  });
  track.addEventListener('mouseleave', () => {
    isHovering = false;
    scheduleAutoplay();
  });

  // Mouse drag-to-scroll, matching live's real (Slick) drag: the track follows the pointer 1:1
  // via transform (not native scrollLeft), reactively re-centering/resizing slides as the drag
  // crosses each slide's midpoint (Slick's own centerMode behavior during drag), then snaps to
  // the nearest slide with an eased settle on release. A small velocity-based fling (advance one
  // slide beyond the nearest if released while still moving fast) approximates Slick's momentum.
  let isDragging = false;
  let startX = 0;
  let startTranslateX = 0;
  let currentTranslateX = 0;
  let lastX = 0;
  let lastTime = 0;
  let velocity = 0;
  let liveIndex = 0;

  const nearestIndexForTranslateX = (x) => {
    let best = liveIndex;
    let bestDiff = Infinity;
    for (let i = 0; i < allSlides.length; i++) {
      const diff = Math.abs(idealTranslateX(i) - x);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = i;
      }
    }
    return best;
  };

  const endDrag = (event) => {
    if (!isPointerDown) return;
    isPointerDown = false;
    if (isDragging) {
      isDragging = false;
      track.classList.remove('is-dragging');
      inner.classList.remove('is-dragging');
      if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);

      let targetIndex = nearestIndexForTranslateX(currentTranslateX);
      const FLING_THRESHOLD = 0.35; // px/ms
      if (velocity < -FLING_THRESHOLD) targetIndex += 1; // dragged left fast -> advance
      else if (velocity > FLING_THRESHOLD) targetIndex -= 1; // dragged right fast -> go back
      goTo(targetIndex, true);

      track.addEventListener('click', (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
      }, { capture: true, once: true });
    }
    scheduleAutoplay();
  };

  // Touch runs through this same drag path. It used to early-return here on
  // `pointerType === 'touch'` with the note "touch already scrolls natively", which was true
  // only while the track was still `overflow-x: auto`; the rebuild to a fixed-width
  // `overflow: hidden` viewport driven by transform (see the track's CSS comment) removed the
  // native scroller, so that branch left finger-swipe doing nothing at all. The track carries
  // `touch-action: pan-y`, which hands horizontal gestures to us and keeps vertical ones with
  // the page -- a vertical/diagonal swipe therefore starts a page scroll and fires
  // `pointercancel`, which `endDrag` already handles.
  track.addEventListener('pointerdown', (event) => {
    isPointerDown = true;
    isDragging = false;
    startX = event.clientX;
    startTranslateX = idealTranslateX(activeIndex);
    currentTranslateX = startTranslateX;
    liveIndex = activeIndex;
    lastX = event.clientX;
    lastTime = performance.now();
    velocity = 0;
    stopAutoplay();
  });

  track.addEventListener('pointermove', (event) => {
    if (!isPointerDown) return;
    const delta = event.clientX - startX;
    if (!isDragging && Math.abs(delta) > 5) {
      isDragging = true;
      track.classList.add('is-dragging');
      inner.classList.add('is-dragging');
      track.setPointerCapture(event.pointerId);
    }
    if (isDragging) {
      const now = performance.now();
      const dt = now - lastTime;
      if (dt > 0) velocity = (event.clientX - lastX) / dt;
      lastX = event.clientX;
      lastTime = now;

      currentTranslateX = startTranslateX + delta;
      const nearest = nearestIndexForTranslateX(currentTranslateX);
      if (nearest !== liveIndex) {
        liveIndex = nearest;
        applyActiveClasses(liveIndex);
      }
      setTransform(currentTranslateX, false);
      event.preventDefault();
    }
  });

  track.addEventListener('pointerup', endDrag);
  track.addEventListener('pointercancel', endDrag);

  // Real bug fixed: dragging starting on top of a slide `<img>` hijacked into the browser's own
  // native image drag-and-drop (ghost image, gesture no longer reaches our pointermove handler)
  // instead of panning the carousel. Slick itself hits the exact same default-browser behavior
  // and works around it with this precise mechanism (confirmed in the vendored slick.min.js
  // source, old-maperce-theme/assets/slick.min.js): bind a capture-phase `dragstart` listener
  // that just calls preventDefault, scoped to slide-track content. Our slides never need real
  // native dragging, so it's unconditional here rather than Slick's `[draggable!=true]` filter.
  track.addEventListener('dragstart', (event) => event.preventDefault());

  // Trackpad two-finger swipe arrives as `wheel` with a horizontal `deltaX`, never as pointer
  // events, so it never reached the drag handler above. Rather than pan the transform per wheel
  // tick (momentum would overshoot unpredictably), accumulate horizontal intent and advance one
  // slide per step through the same `goTo` the dots and autoplay use, so the easing and snap
  // positions stay identical to every other input.
  //
  // Only clearly-horizontal gestures are claimed; anything vertical-dominant is left to the page
  // so the section never traps a normal downward scroll. `passive: false` is required because we
  // preventDefault the claimed ones (otherwise macOS/Chrome treat a horizontal swipe as
  // back/forward navigation).
  const WHEEL_STEP_PX = 60;
  const WHEEL_COOLDOWN_MS = 320; // ~ the transform transition, so steps can't outrun the animation
  let wheelAccum = 0;
  let lastWheelStep = 0;
  let wheelIdleTimer = null;

  track.addEventListener('wheel', (event) => {
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
    event.preventDefault();
    stopAutoplay();

    if (wheelIdleTimer) clearTimeout(wheelIdleTimer);
    wheelIdleTimer = setTimeout(() => {
      wheelAccum = 0;
      scheduleAutoplay();
    }, 150);

    wheelAccum += event.deltaX;
    if (Math.abs(wheelAccum) < WHEEL_STEP_PX) return;

    const now = performance.now();
    if (now - lastWheelStep < WHEEL_COOLDOWN_MS) return;
    lastWheelStep = now;

    goTo(activeIndex + (wheelAccum > 0 ? 1 : -1), true);
    wheelAccum = 0;
  }, { passive: false });

  window.addEventListener('resize', () => {
    readWidths();
    goTo(activeIndex, false);
  });

  readWidths();
  applyActiveClasses(activeIndex);
  setTransform(idealTranslateX(activeIndex), false);
});
